import { mountStoreDevtool } from 'simple-zustand-devtools';
import { create } from 'zustand';

import config from '@src/configs/aspendental';

type OfficeRolloutFeatureFlags = {
	documentsAndRecordsLocations: string[];
	cardAuthorizationLocations: string[];
	sleepApneaLocations: string[];
	enableFraudCheck: string[];
	loginLanguageToggle: boolean;
	enableFormsLanguageToggle: string[];
	enablePortalRedesignAuth: string[];
	enablePortalRedesignNoAuth: boolean;
	enableMarketingConsentInAuthPage: boolean;
	enableAiClinicalTools: string[];
};

type State = {
	restUrl: string;
	graphUrl: string;
	googleUrl: string;
	userEventsUrl: string;
	formEventsUrl: string;
	mottoFlow: boolean;
	denturesFlow: boolean;
	enableFraudCheck: boolean;
	languageToggleEnabled: boolean;
	googleMapApiKey: string;
	isPatientServicesApp: boolean;
	eapiBaseUrl: string;
	previsitPaperworkUrl: string;
	isPatientPortalPhase2Enabled: boolean;
	isPatientPortalNavigationEnabled: boolean;
	officeRollout: OfficeRolloutFeatureFlags;
	userStateCodeForAnalytics: string;
};

type Actions = {
	setRestUrl: (url: string) => void;
	setGraphUrl: (url: string) => void;
	setGoogleUrl: (url: string) => void;
	setUserEventsUrl: (url: string) => void;
	setFormEventsUrl: (url: string) => void;
	setMottoFlow: (active: boolean) => void;
	setDenturesFlow: (active: boolean) => void;
	setLanguageToggleEnabled: (enabled: boolean) => void;
	setGoogleMapApiKey: (apiKey: string) => void;
	setIsPatientServicesApp: (isPatientServicesApp: boolean) => void;
	setEapiBaseUrl: (url: string) => void;
	setPrevisitPaperworkUrl: (url: string) => void;
	setIsPatientPortalPhase2Enabled: (enabled: boolean) => void;
	setIsPatientPortalNavigationEnabled: (enabled: boolean) => void;
	setOfficeRollout: (rollout: OfficeRolloutFeatureFlags) => void;
	setUserStateCodeForAnalytics: (stateCode: string) => void;
	setEnableFraudCheck: (active: boolean) => void;
};

const useAppStore = create<State & Actions>((set) => ({
	restUrl: 'https://apistg.aspendental.com/api/web/', // default to stage
	graphUrl: 'https://apistg.aspendental.com/websitegraphql/graphql/',
	googleUrl: 'https://www.google.com/maps/conversion/debug/collect', // default to stage
	mottoFlow: false,
	denturesFlow: false,
	languageToggleEnabled: false,
	enableFraudCheck: false,
	googleMapApiKey: config.services.nonprod.GOOGLE_MAP_API_KEY,
	setRestUrl: (url) => set({ restUrl: url }),
	setGraphUrl: (url) => set({ graphUrl: url }),
	setGoogleUrl: (url) => set({ googleUrl: url }),
	userEventsUrl: 'https://aspen-userevents.gcp.stage.aspendental.com/', // default to stage
	setUserEventsUrl: (userEventsUrl) => set({ userEventsUrl }),
	formEventsUrl: 'https://aspen-userevents.gcp.stage.aspendental.com/form/events',
	setFormEventsUrl: (formEventsUrl) => set({ formEventsUrl }),
	setMottoFlow: (active) => set({ mottoFlow: active }),
	setDenturesFlow: (active) => set({ denturesFlow: active }),
	setLanguageToggleEnabled: (languageToggleEnabled) => set({ languageToggleEnabled }),
	setGoogleMapApiKey: (googleMapApiKey) => set({ googleMapApiKey }),
	isPatientServicesApp: false,
	setIsPatientServicesApp: (isPatientServicesApp) => set({ isPatientServicesApp }),
	setEnableFraudCheck: (active) => set({ enableFraudCheck: active }),
	eapiBaseUrl: 'https://apistg.aspendental.com/', // default to stage
	setEapiBaseUrl: (eapiBaseUrl) => set({ eapiBaseUrl }),
	officeRollout: {
		documentsAndRecordsLocations: [],
		cardAuthorizationLocations: [],
		sleepApneaLocations: [],
		enableFraudCheck: [],
		loginLanguageToggle: false,
		enableFormsLanguageToggle: [],
		enablePortalRedesignAuth: [],
		enablePortalRedesignNoAuth: false,
		enableMarketingConsentInAuthPage: false,
		enableAiClinicalTools: [],
	},
	setOfficeRollout: (officeRollout) => set({ officeRollout }),
	previsitPaperworkUrl: 'https://visitprepstg.aspendental.com/login?token=', // default to stage
	setPrevisitPaperworkUrl: (url) => set({ previsitPaperworkUrl: url }),
	isPatientPortalPhase2Enabled: false,
	setIsPatientPortalPhase2Enabled: (enabled) => set({ isPatientPortalPhase2Enabled: enabled }),
	isPatientPortalNavigationEnabled: false,
	setIsPatientPortalNavigationEnabled: (enabled) => set({ isPatientPortalNavigationEnabled: enabled }),
	userStateCodeForAnalytics: '',
	setUserStateCodeForAnalytics(stateCode) {
		set({ userStateCodeForAnalytics: stateCode });
	},
}));

if (process.env.NODE_ENV === 'development') {
	mountStoreDevtool('appStore', useAppStore);
}

export default useAppStore;
